import { useLocalStorageContext } from "../Provider/LocalStorageProvider";
import { formatDateWithPattern, parseDateFromFormatted, getCountryDateFormat } from "./DateFormatUtility";
import { useState, useEffect } from "react";

/**
 * Hook to access date formatting utilities with the current user's date format preference
 * or the country-specific date format when available
 */
export const useDateFormatter = (countryName?: string) => {
  const { dateFormat } = useLocalStorageContext();
  const [countryDateFormat, setCountryDateFormat] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Fetch the country's preferred date format when country name is provided
  useEffect(() => {
    let isActive = true;
    
    const fetchCountryDateFormat = async () => {
      setIsLoading(true);
      try {
        if (countryName) {
          const format = await getCountryDateFormat(countryName);
          if (isActive) {
            setCountryDateFormat(format);
            if (format) {
              console.log(`Using country-specific date format for ${countryName}: ${format}`);
            }
          }
        } else {
          if (isActive) {
            setCountryDateFormat(null);
          }
        }
      } catch (error) {
        console.error(`Error fetching date format for ${countryName}:`, error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };
    
    fetchCountryDateFormat();

    return () => {
      isActive = false;
    };
  }, [countryName]);
  
  // Use the country format if available, otherwise fall back to user preference
  const effectiveFormat = countryDateFormat || dateFormat;
  
  /**
   * Format a date according to the country's preferred format or user's preferred format
   */
  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? parseDateFromFormatted(date, effectiveFormat) : date;
    if (!dateObj || isNaN(dateObj.getTime())) {
      throw new Error('Invalid date format');
    }
    return formatDateWithPattern(dateObj, effectiveFormat);
  };
  
  /**
   * Parse a formatted date string according to the country's preferred format or user's preferred format
   */
  const parseDate = (dateStr: string): Date | null => {
    return parseDateFromFormatted(dateStr, effectiveFormat);
  };
  
  /**
   * Get the current effective date format (country-specific or user preference)
   */
  const getCurrentDateFormat = (): string => {
    return effectiveFormat;
  };
  
  return {
    formatDate,
    parseDate,
    getCurrentDateFormat
  };
};

/**
 * Format a date using the provided pattern without requiring the hook
 * Useful for components that don't have access to React context
 */
export const formatDateWithCurrentFormat = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const format = localStorage.getItem('dateFormat') || 'DD-MM-YYYY';
  return formatDateWithPattern(dateObj, format);
};